import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

function getCsrfToken() {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) return metaTag.getAttribute('content');
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const method = (config.method || '').toUpperCase();
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let lastToastTime = {};
const TOAST_COOLDOWN = 3000;

function shouldShowToast(key) {
  const now = Date.now();
  if (lastToastTime[key] && now - lastToastTime[key] < TOAST_COOLDOWN) return false;
  lastToastTime[key] = now;
  return true;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const isLoginPage = window.location.pathname === '/login';

    if (status === 401 && !isLoginPage) {
      localStorage.removeItem('token');
      if (shouldShowToast('auth_expired')) {
        toast.error('Session expired. Please log in again.');
      }
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (status === 403 && shouldShowToast('permission_denied')) {
      toast.error('You do not have permission to perform this action.');
    }

    if (status >= 500 && shouldShowToast('server_error')) {
      toast.error('A server error occurred. Please try again later.');
    }

    if (!error.response && error.code === 'ERR_NETWORK' && shouldShowToast('network_error')) {
      toast.error('Network error. Please check your internet connection.');
    }

    return Promise.reject(error);
  }
);

export async function retryRequest(fn, maxRetries = 3, delay = 1000) {
  let lastError;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) throw err;
      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
      }
    }
  }
  throw lastError;
}

export default api;
